/**
 * Cart Drawer Discount Code Handler
 * Handles multiple discount codes with product dependency validation
 * Auto-removes invalid coupons when cart changes
 */

(function () {
  "use strict";

  const selectors = {
    discountContainer: "[data-drawer-discount]",
    discountInput: "[data-discount-input]",
    applyButton: "[data-discount-apply]",
    messageArea: "[data-discount-message]",
    discountList: "[data-discount-list]",
    discountsDisplay: "[data-discounts]",
    subtotalDisplay: "[data-subtotal]",
    productsContainer: "[data-products]",
    toastContainer: "[data-cart-drawer-toast]",
  };

  const classes = {
    loading: "is-loading",
    success: "is-success",
    error: "is-error",
    visible: "is-visible",
    hidden: "hide",
    toast: "cart-drawer-toast",
    toastEntering: "cart-drawer-toast--entering",
    toastExiting: "cart-drawer-toast--exiting",
  };

  const messages = {
    applied: "Discount applied successfully! 🎉",
    removed: "Discount removed",
    duplicate: "This discount code is already applied",
    invalid: "Invalid or inapplicable discount code",
    empty: "Please enter a discount code",
    error: "Something went wrong. Please try again.",
    autoRemoved: "Discount removed due to cart changes",
  };

  class CartDiscount {
    constructor() {
      this.container = document.querySelector(selectors.discountContainer);

      if (!this.container) {
        return;
      }

      this.input = this.container.querySelector(selectors.discountInput);
      this.applyBtn = this.container.querySelector(selectors.applyButton);
      this.messageArea = this.container.querySelector(selectors.messageArea);
      this.discountList = this.container.querySelector(selectors.discountList);
      this.toastContainer = document.querySelector(selectors.toastContainer);
      
      // Store current toast timeout
      this.currentToastTimeout = null;

      // Store applied discount codes with metadata
      this.appliedCodes = [];
      
      // Store cart items for validation
      this.cartItems = [];

      // Debounce timer for validation
      this.validationTimer = null;

      // Debounce timer for cart updates to batch rapid changes
      this.updateTimer = null;

      // Debounce timer for syncing discounts to prevent flicker
      this.syncTimer = null;

      // Flag to prevent rapid re-renders that cause flicker
      this.isRendering = false;

      this.init();
    }

    init() {
      if (!this.input || !this.applyBtn) {
        return;
      }

      // Bind event listeners
      this.applyBtn.addEventListener("click", this.handleApply.bind(this));

      this.input.addEventListener("keypress", (e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          this.handleApply();
        }
      });

      document.addEventListener("cart:updated", this.handleCartUpdate.bind(this));
      
      document.addEventListener("cart:change", this.handleCartChange.bind(this));

      document.addEventListener("ajaxProduct:added", this.handleCartUpdate.bind(this));
      document.addEventListener("product-form:cart:update", this.handleCartUpdate.bind(this));

      document.addEventListener("cart:build", this.handleCartUpdate.bind(this));

      this.loadCurrentDiscounts();
    }

    showLoader() {
      if (this.applyBtn) {
        this.applyBtn.disabled = true;
        this.applyBtn.classList.add(classes.loading);
      }
      if (this.input) {
        this.input.disabled = true;
      }
    }

    hideLoader() {
      if (this.applyBtn) {
        this.applyBtn.disabled = false;
        this.applyBtn.classList.remove(classes.loading);
      }
      if (this.input) {
        this.input.disabled = false;
      }
    }

    showMessage(message, type = "info") {
      // For success and error, show toast notification only
      if (type === "success" || type === "error") {
        this.showToast(message, type);
      }
      
      // Keep inline message area hidden - only toast is used
      if (this.messageArea) {
        this.messageArea.style.display = "none";
        this.messageArea.classList.remove(classes.visible);
      }
    }

    showToast(message, type) {
      if (!this.toastContainer) return;

      // Remove all existing toasts immediately so repeated clicks never stack
      this.toastContainer.querySelectorAll(`.${classes.toast}`).forEach((t) => t.remove());

      if (this.currentToastTimeout) {
        clearTimeout(this.currentToastTimeout);
        this.currentToastTimeout = null;
      }

      requestAnimationFrame(() => {
        const toast = document.createElement("div");
        toast.className = `${classes.toast} ${classes.toast}--${type}`;
        toast.textContent = message;
        toast.setAttribute("role", "alert");
        toast.setAttribute("aria-live", "polite");
        this.toastContainer.appendChild(toast);

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            toast.classList.add(classes.toastEntering);
          });
        });

        this.currentToastTimeout = setTimeout(() => {
          this.hideToast();
        }, 5000);
      });
    }

    hideToast() {
      if (!this.toastContainer) return;

      const existingToast = this.toastContainer.querySelector(`.${classes.toast}`);
      if (existingToast) {
        requestAnimationFrame(() => {
          existingToast.classList.remove(classes.toastEntering);
          existingToast.classList.add(classes.toastExiting);

          setTimeout(() => {
            requestAnimationFrame(() => {
              if (existingToast.parentNode) {
                existingToast.parentNode.removeChild(existingToast);
              }
            });
          }, 250); 
        });
      }

      if (this.currentToastTimeout) {
        clearTimeout(this.currentToastTimeout);
        this.currentToastTimeout = null;
      }
    }

    hideMessage() {
      if (this.messageArea) {
        this.messageArea.classList.remove(classes.visible);
        setTimeout(() => {
          if (!this.messageArea.classList.contains(classes.visible)) {
            this.messageArea.style.display = "none";
          }
        }, 200); 
      }
    }

    clearMessage() {
      this.hideToast();
      
      if (this.messageArea) {
        this.messageArea.textContent = "";
        this.messageArea.classList.remove(
          classes.visible,
          classes.success,
          classes.error
        );
        this.messageArea.style.display = "none";
      }
    }

    async handleApply() {
      const fromPopup = this.applyBtn.dataset.popupTriggered === "true";
      const replaceDiscount = this.applyBtn.dataset.replaceDiscount === "true";
      delete this.applyBtn.dataset.popupTriggered;
      delete this.applyBtn.dataset.replaceDiscount;

      const code = this.input.value.trim().toUpperCase();

      if (!fromPopup) {
        if (!code) {
          this.showMessage(messages.empty, "error");
          return;
        }

        if (!replaceDiscount && this.appliedCodes.some(c => c.code.toUpperCase() === code)) {
          this.showMessage(messages.duplicate, "error");
          this.input.value = "";
          return;
        }
      }

      await this.applyDiscount(code, fromPopup, replaceDiscount);
    }

    async applyDiscount(code, fromPopup = false, replaceDiscount = false) {
      if (!fromPopup) this.showLoader();
      this.clearMessage();

      try {
        // Replace mode: send only the new code (switch); otherwise stack with existing codes
        const allCodes = replaceDiscount ? [code] : [...this.appliedCodes.map(c => c.code), code];
        const discountString = allCodes.join(",");

        const response = await fetch(
          `${window.Shopify.routes.root}cart/update.js`,
          {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              discount: discountString,
            }),
          }
        );

        const cart = await response.json();

        if (!response.ok) {
          this.input.value = "";
          if (!fromPopup) this.hideLoader();
          return;
        }

        // Refresh the cart drawer UI
        await this.refreshCartDrawer();

        // Fetch updated cart to verify discount was applied
        const updatedCart = await this.getCart();

        // Update cart items for validation
        this.cartItems = updatedCart.items || [];

        // Check if the new code was applied (cart-level OR product/line-level)
        const codeApplied = this.isDiscountCodeApplied(updatedCart, code);

        if (!codeApplied) {
          this.input.value = "";
          await this.syncDiscountsFromCart(updatedCart);
          if (!fromPopup) this.showMessage(messages.invalid, "error");
          return;
        }

        // Clear input on success
        this.input.value = "";

        // Sync all discounts from cart
        await this.syncDiscountsFromCart(updatedCart);

        if (!fromPopup) {
          // Show success message
          this.showMessage(messages.applied, "success");
        }

        // Dispatch cart updated event
        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { cart: updatedCart },
          })
        );
      } catch (error) {
        console.error("Error applying discount:", error);
        if (!fromPopup) this.showMessage(messages.error, "error");
        this.input.value = "";
      } finally {
        if (!fromPopup) this.hideLoader();
      }
    }

    async getCart() {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`, {
        credentials: "same-origin",
        method: "GET",
      });
      return response.json();
    }

    /**
     * Check if a discount code is applied at cart-level OR line-level (product-level).
     * Product-level codes appear in line_level_discount_allocations, not in cart_level_discount_applications.
     */
    isDiscountCodeApplied(cart, code) {
      const upperCode = code.toUpperCase();
      const cartLevel = cart.cart_level_discount_applications || [];
      if (cartLevel.some((d) => (d.title || "").toUpperCase() === upperCode)) {
        return true;
      }
      const items = cart.items || [];
      for (let i = 0; i < items.length; i++) {
        const allocations = items[i].line_level_discount_allocations || items[i].discount_allocations || [];
        if (allocations.some((a) => (a.discount_application && (a.discount_application.title || "").toUpperCase() === upperCode))) {
          return true;
        }
      }
      return false;
    }

    async removeDiscount(code) {
      this.showLoader();
      this.clearMessage();

      try {
        // Remove the specific code from the list
        const remainingCodes = this.appliedCodes
          .filter(c => c.code.toUpperCase() !== code.toUpperCase())
          .map(c => c.code);

        const discountString = remainingCodes.length > 0 ? remainingCodes.join(",") : "";

        const response = await fetch(
          `${window.Shopify.routes.root}cart/update.js`,
          {
            method: "POST",
            credentials: "same-origin",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            body: JSON.stringify({
              discount: discountString,
            }),
          }
        );

        const cart = await response.json();

        if (!response.ok) {
          throw new Error(cart.message || messages.error);
        }

        // Refresh the cart drawer UI
        await this.refreshCartDrawer();

        // Fetch updated cart
        const updatedCart = await this.getCart();
        
        // Update cart items
        this.cartItems = updatedCart.items || [];

        // Sync discounts
        await this.syncDiscountsFromCart(updatedCart);

        // Do not show toast when discount is removed (per requirements)
        // Only clear the message area
        this.clearMessage();

        // Dispatch cart updated event
        document.dispatchEvent(
          new CustomEvent("cart:updated", {
            detail: { cart: updatedCart },
          })
        );
      } catch (error) {
        console.error("Error removing discount:", error);
        this.showMessage(error.message || messages.error, "error");
      } finally {
        this.hideLoader();
      }
    }

    async refreshCartDrawer() {
      // Use the theme's existing cart build method if available
      if (
        typeof theme !== "undefined" &&
        theme.cart &&
        theme.cart.getCartProductMarkup
      ) {
        // Trigger cart build event which the theme listens to
        document.dispatchEvent(new CustomEvent("cart:build"));
      } else {
        // Fallback: manually refresh cart drawer content
        await this.manualCartRefresh();
      }
    }

    async manualCartRefresh() {
      try {
        const url = `${
          window.Shopify.routes.root
        }cart?view=ajax&t=${Date.now()}`;
        const response = await fetch(url, {
          credentials: "same-origin",
          method: "GET",
        });
        const html = await response.text();

        // Parse the HTML and update relevant sections
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const newItems = doc.querySelector(".cart__items");
        const newDiscounts = doc.querySelector(".cart__discounts");

        // Update products
        const productsContainer = document.querySelector(
          selectors.productsContainer
        );
        if (productsContainer && newItems) {
          productsContainer.innerHTML = "";
          productsContainer.appendChild(newItems);

          // Update subtotal from data attribute
          const subtotalEl = document.querySelector(selectors.subtotalDisplay);
          if (subtotalEl && newItems.dataset.cartSubtotal) {
            subtotalEl.innerHTML = this.formatMoney(
              parseInt(newItems.dataset.cartSubtotal)
            );
          }
        }

        // Update discounts display
        const discountsContainer = document.querySelector(
          selectors.discountsDisplay
        );
        if (discountsContainer && newDiscounts) {
          discountsContainer.innerHTML = newDiscounts.innerHTML;
        }
      } catch (error) {
        console.error("Error refreshing cart:", error);
      }
    }

    formatMoney(cents) {
      // Use theme's money formatter if available
      if (
        typeof theme !== "undefined" &&
        theme.Currency &&
        theme.Currency.formatMoney
      ) {
        return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
      }
      // Fallback basic formatter
      return "$" + (cents / 100).toFixed(2);
    }

    async syncDiscountsFromCart(cart) {
      // Clear any pending sync to batch rapid updates and prevent flicker
      clearTimeout(this.syncTimer);
      
      // Debounce the sync to prevent multiple rapid renders
      this.syncTimer = setTimeout(() => {
        const discountApplications = cart.cart_level_discount_applications || [];
        const codeToAmount = new Map();

        // Cart-level discounts
        discountApplications.forEach(discount => {
          const code = discount.title;
          if (code) {
            codeToAmount.set(code.toUpperCase(), {
              code: discount.title,
              amount: discount.total_allocated_amount || 0,
              type: discount.target_type || "cart",
            });
          }
        });

        // Line-level (product-level) discounts: aggregate by code
        const items = cart.items || [];
        items.forEach(item => {
          const allocations = item.line_level_discount_allocations || item.discount_allocations || [];
          allocations.forEach(alloc => {
            const app = alloc.discount_application;
            if (!app || !app.title) return;
            const code = app.title.toUpperCase();
            const amount = alloc.amount || 0; // already total for this line
            const existing = codeToAmount.get(code);
            if (existing) {
              existing.amount += amount;
            } else {
              codeToAmount.set(code, {
                code: app.title,
                amount: amount,
                type: "line_item",
              });
            }
          });
        });

        this.appliedCodes = Array.from(codeToAmount.values());

        // Render the discount list (with flicker prevention built-in)
        this.renderDiscountList();
      }, 10); // Very short delay to batch rapid calls
    }

    renderDiscountList() {
      if (!this.discountList) return;

      // Prevent rapid re-renders that cause flicker
      if (this.isRendering) {
        return;
      }

      this.isRendering = true;

      // Use requestAnimationFrame for smooth, flicker-free updates
      requestAnimationFrame(() => {
        try {
          // Get current codes from DOM to compare
          const existingItems = Array.from(this.discountList.children);
          const existingCodes = existingItems.map(item => 
            item.getAttribute('data-discount-code')
          ).filter(Boolean);

          // Get new codes
          const newCodes = this.appliedCodes.map(d => d.code);

          // Check if anything actually changed (skip re-render if identical)
          const codesChanged = 
            existingCodes.length !== newCodes.length ||
            existingCodes.sort().join(',') !== newCodes.sort().join(',');

          if (!codesChanged) {
            // Nothing changed, no need to re-render
            this.isRendering = false;
            return;
          }

          // Use DocumentFragment for smoother DOM updates (prevents flicker)
          const fragment = document.createDocumentFragment();

          // Build new items
          this.appliedCodes.forEach(discount => {
            const code = this.escapeHtml(discount.code);
            
            // Check if item already exists
            const existingItem = existingItems.find(item => 
              item.getAttribute('data-discount-code') === code
            );

            if (existingItem) {
              // Reuse existing element (no flicker)
              fragment.appendChild(existingItem);
            } else {
              // Create new element
              const item = document.createElement('div');
              item.className = 'cart-discount-code-item';
              item.setAttribute('data-discount-code', code);
              item.innerHTML = `
                <span class="cart-discount-code-item-text">${code}</span>
                <button 
                  type="button" 
                  class="cart-discount-code-item-remove"
                  data-remove-discount="${code}"
                  aria-label="Remove discount ${code}"
                >
                  <svg aria-hidden="true" focusable="false" viewBox="0 0 64 64">
                    <path d="m19 17.61 27.12 27.13m0-27.12L19 44.74" stroke="currentColor" stroke-width="4" fill="none"/>
                  </svg>
                </button>
              `;
              fragment.appendChild(item);
            }
          });

          // Remove items that are no longer needed
          existingItems.forEach(item => {
            const code = item.getAttribute('data-discount-code');
            if (!newCodes.includes(code)) {
              item.remove();
            }
          });

          // Clear and append new content in one operation (minimizes reflow)
          if (this.discountList.children.length === 0) {
            // Empty list - just append
            this.discountList.appendChild(fragment);
          } else {
            // Replace existing items efficiently
            while (this.discountList.firstChild) {
              this.discountList.removeChild(this.discountList.firstChild);
            }
            this.discountList.appendChild(fragment);
          }

          // Attach event listeners to all remove buttons in main list
          this.discountList.querySelectorAll("[data-remove-discount]").forEach(btn => {
            const code = btn.getAttribute('data-remove-discount');
            // Remove existing listener if any, then add new one
            const newBtn = btn.cloneNode(true);
            btn.parentNode.replaceChild(newBtn, btn);
            newBtn.addEventListener("click", (e) => {
              e.preventDefault();
              this.removeDiscount(code);
            });
          });

          // Mirror discount list into any popup lists so coupons drawer shows applied codes
          const popupLists = document.querySelectorAll(".cart-discount-code-list--popup");
          popupLists.forEach((popupList) => {
            // Copy markup
            popupList.innerHTML = this.discountList.innerHTML;

            // Attach remove handlers inside popup as well
            popupList.querySelectorAll("[data-remove-discount]").forEach(btn => {
              const code = btn.getAttribute("data-remove-discount");
              const newBtn = btn.cloneNode(true);
              btn.parentNode.replaceChild(newBtn, btn);
              newBtn.addEventListener("click", (e) => {
                e.preventDefault();
                this.removeDiscount(code);
              });
            });
          });

        } finally {
          this.isRendering = false;
        }
      });
    }

    escapeHtml(text) {
      const div = document.createElement("div");
      div.textContent = text;
      return div.innerHTML;
    }

    async loadCurrentDiscounts() {
      try {
        const cart = await this.getCart();
        
        // Store cart items
        this.cartItems = cart.items || [];

        // Sync discounts
        await this.syncDiscountsFromCart(cart);
      } catch (error) {
        console.error("Error loading discounts:", error);
      }
    }

    async handleCartUpdate(event) {
      // Always fetch the latest cart state to ensure we have complete discount information
      // This is important because when products are added/removed, the cart is rebuilt
      // and we need to refresh the discount list to show applied coupon names
      try {
        const cart = await this.getCart();
        
        // Update cart items
        this.cartItems = cart.items || [];

        // Sync discounts from the latest cart state
        await this.syncDiscountsFromCart(cart);
      } catch (error) {
        console.error("Error handling cart update:", error);
        // Fallback: try to use event data if available
        if (event && event.detail && event.detail.cart) {
          const cart = event.detail.cart;
          this.cartItems = cart.items || [];
          await this.syncDiscountsFromCart(cart);
        }
      }
    }

    async handleCartChange(event) {
      // Debounce validation to avoid excessive API calls
      clearTimeout(this.validationTimer);
      
      this.validationTimer = setTimeout(async () => {
        await this.validateDiscounts();
      }, 500);
    }

    async validateDiscounts() {
      try {
        // Fetch current cart state
        const cart = await this.getCart();
        
        // Update cart items
        const currentItems = cart.items || [];
        const currentDiscounts = cart.cart_level_discount_applications || [];

        // Build current codes from both cart-level and line-level (product-level) discounts
        const currentCodesSet = new Set((currentDiscounts || []).map(d => (d.title || "").toUpperCase()));
        (currentItems || []).forEach(item => {
          const allocations = item.line_level_discount_allocations || item.discount_allocations || [];
          allocations.forEach(alloc => {
            const title = alloc.discount_application && alloc.discount_application.title;
            if (title) currentCodesSet.add(title.toUpperCase());
          });
        });
        const currentCodes = Array.from(currentCodesSet);

        // Check if any discounts are no longer valid
        const previousCodes = this.appliedCodes.map(c => c.code.toUpperCase());

        // Find codes that were removed automatically by Shopify
        const removedCodes = previousCodes.filter(code => !currentCodes.includes(code));

        if (removedCodes.length > 0) {
          // Update UI to reflect auto-removed discounts
          await this.syncDiscountsFromCart(cart);
          
          // Show message about auto-removal
          this.showMessage(messages.autoRemoved, "error");

          // Refresh cart drawer
          await this.refreshCartDrawer();
        } else {
          // Just sync the current state
          this.cartItems = currentItems;
          await this.syncDiscountsFromCart(cart);
        }
      } catch (error) {
        console.error("Error validating discounts:", error);
      }
    }
  }

  // Initialize when DOM is ready
  function init() {
    new CartDiscount();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  // Re-initialize when cart drawer opens (for dynamic content)
  document.addEventListener("cart:open", () => {
    setTimeout(() => {
      new CartDiscount();
    }, 100);
  });

  // Expose globally for potential theme integration
  window.CartDiscount = CartDiscount;
})();